import * as React from 'react';
import {MOBILE_PICKER_HEIGHT} from './constants';
import {getMobileOptionStyles, getWheelSpacerHeight} from './select.utils';
import {type SelectValue} from './types';
import {type SelectOption} from '../../lib/selector-option';
import {cn} from '../../lib/cn';

type AirbnbSelectMobileWheelProps<
  T,
  V extends SelectValue,
  L extends string | number | React.ReactNode,
> = {
  id: string;
  options: SelectOption<T, V, L>[];
  labelledBy: string;
  describedBy?: string;
  disabled?: boolean;
  menuClassName?: string;
  scrollTop: number;
  activeIndex?: number;
  listRef: React.RefObject<HTMLDivElement>;
  onScroll: () => void;
  onOptionClick: (option: SelectOption<T, V, L>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  getOptionId: (index: number) => string;
  noOptionsMessage?: () => string | undefined;
};

function AirbnbSelectMobileWheel<
  T,
  V extends SelectValue,
  L extends string | number | React.ReactNode,
>({
  id,
  options,
  labelledBy,
  describedBy,
  disabled,
  menuClassName,
  scrollTop,
  activeIndex,
  listRef,
  onScroll,
  onOptionClick,
  onKeyDown,
  getOptionId,
  noOptionsMessage,
}: AirbnbSelectMobileWheelProps<T, V, L>) {
  const spacerHeight = getWheelSpacerHeight();
  const emptyMessage = noOptionsMessage?.();

  return (
    <div
      id={id}
      role="listbox"
      tabIndex={0}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-activedescendant={
        activeIndex !== undefined && activeIndex >= 0
          ? getOptionId(activeIndex)
          : undefined
      }
      onKeyDown={onKeyDown}
      className={cn('relative overflow-hidden outline-none', menuClassName)}
    >
      {options.length === 0 && emptyMessage ? (
        <div className="flex min-h-[160px] items-center justify-center px-4 text-center text-base leading-6 text-[var(--chekin-airbnb-gray-text)]">
          {emptyMessage}
        </div>
      ) : null}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white via-white/80 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent" />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 top-1/2 z-0 h-8 -translate-y-1/2 rounded-[8px] bg-black/[0.04]',
          options.length === 0 && emptyMessage && 'hidden',
        )}
      />
      <div
        ref={listRef}
        onScroll={onScroll}
        className={cn(
          'relative z-10 snap-y snap-mandatory overflow-y-auto text-center [-ms-overflow-style:none] [mask-image:linear-gradient(to_bottom,transparent_0%,black_18%,black_82%,transparent_100%)] [scrollbar-width:none]',
          options.length === 0 && emptyMessage && 'hidden',
        )}
        style={{
          height: `${MOBILE_PICKER_HEIGHT}px`,
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div style={{height: `${spacerHeight}px`}} />
        {options.map((option, index) => {
          const {distance, style} = getMobileOptionStyles(index, scrollTop);
          const optionKey = `${String(option.value)}-${index}`;

          return (
            <button
              id={getOptionId(index)}
              key={optionKey}
              type="button"
              role="option"
              aria-selected={distance < 0.5}
              aria-disabled={disabled || option.isDisabled}
              tabIndex={-1}
              disabled={disabled || option.isDisabled}
              onClick={() => onOptionClick(option)}
              className={cn(
                'flex w-full snap-center items-center justify-center overflow-hidden truncate whitespace-nowrap px-4 text-center font-normal touch-manipulation',
                (disabled || option.isDisabled) && 'cursor-not-allowed opacity-40',
              )}
              style={style}
            >
              {String(option.label)}
            </button>
          );
        })}
        <div style={{height: `${spacerHeight}px`}} />
      </div>
    </div>
  );
}

export {AirbnbSelectMobileWheel};
